import Box from "@mui/material/Box";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import lodash from "lodash";
import { forwardRef, useCallback, useMemo } from "react";

import { PatientObservationChart, tabsWithOnlyTable } from "@/pages/patient/patient-observation-chart";
import {
  bedsideTableSections,
  neurologicalTableSections,
  urinalysisTableSections,
  ventilatorTableSections,
  vitalsSignsTableSections,
} from "@/components/ObservationChart";
import { WithObsChartCtx } from "@/components/ObservationChart/ObservationChart_ctx";
import { WithObsTableCtx } from "@/components/ObservationChart/ObservationTable_ctx";
import {
  ObsTabs,
  tabNames,
  useObsTabsCtx,
} from "@/components/ObservationChart/ObservationTabs_ctx";
import ObservationSummary from "@/components/ObservationSummary/ObservationSummary";
import { PatientTasks } from "@/components/PatientTasks";
import TabSection, { TabSubSection } from "@/components/TabSection";
import { Observation, Patient, PatientEncounter, Task } from "@/resources/types";
import { Theme, css } from "@/theme";
import global from "@/global";
import { useAddObservationModal } from "@/modals/addObservation";

const styles = {
  container: css`
    border-radius: 1rem;
    padding-bottom: 1rem;
  `,
  tabPanel: css`
    background-color: #fafbff;
  `,
  heading: (theme: Theme) => css`
    background-color: ${theme.palette.primary[100]};
    border-radius: 1rem 1rem 0px 0px;
    color: ${theme.palette.primary[900]};
    padding: 0 2rem;
    margin-bottom: -0.25rem;

    .MuiTabs-flexContainer {
      gap: 2rem;
    }

    .MuiTab-root {
      padding: 0;
    }
  `,
};

type PatientObservationTabsProps = {
  encounter: PatientEncounter;
  handleTasksChange: (newTasks: Task[]) => void;
  tasks: Task[];
  patient: Patient;
};

const TABLE_SECTIONS = {
  [ObsTabs.VITALS_SIGNS]: vitalsSignsTableSections,
  [ObsTabs.BEDSIDE]: bedsideTableSections,
  [ObsTabs.VENTILATOR]: ventilatorTableSections,
  [ObsTabs.URINALYSIS]: urinalysisTableSections,
  [ObsTabs.NEUROLOGICAL]: neurologicalTableSections,
};

// Maps observation types to their relevant "keys"
const OBSERVATION_KEYS = {
  [ObsTabs.VITALS_SIGNS]: [
    "respirations",
    "o2Sats",
    "o2Flow",
    "heartRate",
    "bloodPressure",
    "temperature",
    "consciousness",
    // "riskScore",
    "bloodGlucose",
    "bloodAlcohol",
    "painScore",
    "rhythm",
  ],
  [ObsTabs.BEDSIDE]: ["bedside"],
  [ObsTabs.VENTILATOR]: ["ventilator"],
  [ObsTabs.URINALYSIS]: ["urinalysis"],
  [ObsTabs.NEUROLOGICAL]: ["neuro"],
};

/**
 * Remove observations that don't have relevant keys for the given observation
 * type. For example, if the observation has no "bedside" object then it is
 * not useful as a Bedside observation.
 */
const filterObs = (observations: Observation[], type: ObsTabs) =>
  observations.filter((ob) => {
    const strippedOb = lodash.pick(ob, OBSERVATION_KEYS[type]);
    return lodash.some(strippedOb, (val: any) => val !== undefined);
  });

const PatientObservationTabs = forwardRef<
  HTMLTableElement,
  PatientObservationTabsProps
>((props, ref) => {
  const { encounter, handleTasksChange, tasks, patient } = props;
  const unfilteredObs = encounter.observations;
  const tasksDisabled = ["disabled", "discharged"].includes(encounter.status);
  const openAddObservation = useAddObservationModal();

  const {
    value: { tab },
    setTab,
  } = useObsTabsCtx();

  const handleEditObservation = (observation: Observation) => {
    openAddObservation({
      mode: "edit",
      encounter: encounter,
      observation: observation,
    });
  };

  const handleChange = useCallback(
    (_event: React.SyntheticEvent, newValue: number) => {
      setTab(Object.keys(tabNames)[newValue] as ObsTabs);
    },
    [setTab, tabNames]
  );

  const currentTab = useMemo(
    () =>
      Object.keys(tabNames).findIndex((val) => {
        return val === tab;
      }),
    [tabNames, tab]
  );

  const observations = useMemo(
    () => filterObs(unfilteredObs || [], tab).filter((obs) => !obs.invalid),
    [unfilteredObs, tab]
  );

  return (
    <TabSection first>
      <Box css={styles.heading}>
        <Tabs value={currentTab} onChange={handleChange}>
          {Object.values(tabNames).map((tabName, index) => (
            <Tab disableRipple key={index} label={tabName} />
          ))}
        </Tabs>
      </Box>
      <TabSubSection>
        <PatientTasks
          onChange={handleTasksChange}
          value={tasks}
          mode="simple"
          disabled={tasksDisabled}
        />
      </TabSubSection>

      <WithObsChartCtx>
        {/* summary bar */}
        {
          (observations && observations.length > 0 && (tab !== ObsTabs.NEUROLOGICAL || global.config.app.enableNeuro !== "false"))
            ? (
              <TabSubSection>
                <ObservationSummary
                  showGraphs={!tabsWithOnlyTable.includes(tab)}
                  showChartLineColors={!tabsWithOnlyTable.includes(tab)}
                  observations={observations}
                />
              </TabSubSection>
            )
            : null
        }
        {/* full chart */}
        <WithObsTableCtx
          observations={observations}
        >
          <PatientObservationChart
            ref={ref}
            observations={observations}
            patient={patient}
            tableSections={TABLE_SECTIONS[tab]}
            encounterId={encounter.id}
            onEditObservation={handleEditObservation}
          />
        </WithObsTableCtx>
      </WithObsChartCtx>
    </TabSection>
  );
});

PatientObservationTabs.displayName = "PatientObservationTabs";

export default PatientObservationTabs;
